import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useBackdropDismiss from '../hooks/useBackdropDismiss';
import { Icon } from '../design-system/icons.jsx';
export default function BulkActionBar({ selectedCount, actions = [], onClear, loading = false, result = null }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(null);
  const confirmBackdrop = useBackdropDismiss(() => setConfirming(null));

  useEffect(() => {
    if (!confirming) return;
    const handler = (e) => { if (e.key === 'Escape') setConfirming(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [confirming]);

  if (selectedCount === 0) return null;

  const handleAction = (action) => {
    if (action.confirm) {
      setConfirming(action);
    } else {
      action.onClick();
    }
  };

  return (
    <>
      <div style={{
        position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-xl)',
        padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        zIndex: 'var(--z-toast)',
        animation: 'slideUp var(--dur-base) var(--ease-out)',
        minWidth: 300, maxWidth: '90vw',
      }}>
        <span style={{
          fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semi)',
          color: 'var(--text-primary)', flexShrink: 0, whiteSpace: 'nowrap',
        }}>
          {selectedCount} selected
        </span>

        <div style={{ width: 1, height: 18, background: 'var(--border)', flexShrink: 0 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
              <Icon name="loading" size={14} strokeWidth={2} style={{ animation: 'spin 0.65s linear infinite' }} />
              Processing…
            </span>
          ) : result ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-sm)' }}>
              {result.failed > 0
                ? <Icon name="warning" size={14} strokeWidth={2} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                : <Icon name="success" size={14} strokeWidth={2} style={{ color: 'var(--success)', flexShrink: 0 }} />
              }
              <span style={{ color: result.failed > 0 ? 'var(--warning)' : 'var(--success)' }}>
                {result.success} done
                {result.failed > 0 ? `, ${result.failed} failed` : ''}
                {result.skipped_running > 0 ? `, ${result.skipped_running} already running` : ''}
              </span>
            </span>
          ) : (
            actions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => handleAction(action)}
                className={`btn btn-sm ${action.danger ? 'btn-danger' : 'btn-secondary'}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                {action.icon}
                {action.label}
              </button>
            ))
          )}
        </div>

        <button
          type="button"
          onClick={onClear}
          title={t('common.a11y.clearSelection')}
          style={{
            marginLeft: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 24, height: 24, borderRadius: 'var(--radius-sm)', flexShrink: 0,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', transition: 'color var(--dur-fast)',
          }}
        >
          <Icon name="close" size={14} strokeWidth={2} />
        </button>
      </div>

      {confirming && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-confirm-title"
          style={{
            position: 'fixed', inset: 0,
            background: 'var(--overlay-modal)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 'calc(var(--z-toast) + 10)',
            padding: 20,
          }}
          {...confirmBackdrop}
        >
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-bright)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-xl)',
            padding: '24px', maxWidth: 420, width: '100%',
            animation: 'slideUp var(--dur-base) var(--ease-out)',
          }}>
            <h3 id="bulk-confirm-title" style={{ fontSize: 'var(--fs-base)', fontWeight: 'var(--fw-bold)', margin: '0 0 8px', color: 'var(--text-primary)' }}>
              {confirming.confirmTitle || `Confirm ${confirming.label}`}
            </h3>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', margin: '0 0 20px', lineHeight: 'var(--lh-base)' }}>
              {(confirming.confirmMessage || 'This action will affect {count} item{plural}.')
                .replace('{count}', selectedCount)
                .replace('{plural}', selectedCount !== 1 ? 's' : '')}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirming(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-sm ${confirming.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => { confirming.onClick(); setConfirming(null); }}
              >
                {confirming.confirmLabel || confirming.label}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
